/*
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import React, { useCallback, useEffect, useState } from 'react';
import appTheme from '../../common/theme/appTheme.json';
import { Box, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Organization } from '../../../shared/entities/organization';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import CustomDataGrid from '../../common/data_grid/CustomDataGrid';
import ModalDialog from '../../common/dialogs/ModalDialog';
import WarningIcon from '@mui/icons-material/Warning';
import { Link, useRouteMatch } from 'react-router-dom';
import ErrorMessage from '../../common/layout/ErrorMessage';
import { StyledProvisioningPageHeading } from '../StyledProvisioningPageHeading';
import ModalLoadingSpinner from '../../common/layout/ModalLoadingSpinner';
import CreateIcon from '@mui/icons-material/Create';
import { Tooltip } from '@mui/material';
import { ajaxRaw } from '../../../utils/StoreUtils';
import {TopAlignedChipCell} from "../../common/data_grid/ChipCell";

interface OrganizationsTableProps {
  disableVirtualization?: boolean;
}

const OrganizationsTable = ({ disableVirtualization = false }: OrganizationsTableProps) => {
  const { path } = useRouteMatch();
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [organizationsArray, setOrganizationsArray] = useState<Partial<Organization>[]>([]);
  const [error, setError] = useState<string | undefined>(undefined);
  const [deleteModalTarget, setDeleteModalTarget] = useState<Partial<Organization> | undefined>(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const loadTable = () => {
    setLoadingOrganizations(true);
    ajaxRaw.get(`/provisioning/organizations`).subscribe({
      next: value => {
        setLoadingOrganizations(false);
        setOrganizationsArray(value.response);
      },
      error: err => {
        setLoadingOrganizations(false);
        setError(err.message);
      },
    });
  };
  useEffect(loadTable, []);

  const columns: GridColDef[] = [
    { field: 'orgNum', headerName: 'ID', flex: 1 },
    { field: 'name', headerName: 'Name', width: 200 },
    {
      field: 'customerGroups',
      headerName: 'Customer group(s)',
      flex: 1,
      renderCell: params => {
        params.value = params.value?.map(v => v.toUpperCase());
        return <TopAlignedChipCell params={params} />;
      },
      sortable: false,
    },
    {
      field: 'usersInOrg',
      headerName: 'User(s)',
      flex: 1,
      valueGetter: params => params.row.usersInOrg.length,
      valueFormatter: params => `${params.value} assigned`,
    },
    {
      field: 'roles',
      headerName: 'Role(s)',
      flex: 1,
      valueGetter: params => params.row.roles.length,
      valueFormatter: params => `${params.value} assigned`,
    },
    {
      field: 'terminalRestrictedMap',
      headerName: 'Terminal(s)',
      flex: 1,
      valueGetter: params => Object.values(params.row.terminalRestrictedMap).length,
      valueFormatter: params => `${params.value} assigned`,
    },
    {
      field: 'actions',
      headerName: '',
      type: 'actions',
      width: 300,
      align: 'right',
      getActions: params => [
        <GridActionsCellItem
          key={'create-org'}
          icon={<CreateIcon />}
          component={Link}
          to={`${path}/edit/${params.row.id}`}
        />,
        <Tooltip
          title={params.row.usersInOrg.length > 0 ? 'Cannot delete organization with assigned users.' : ''}
          placement="top"
        >
          <span>
            <GridActionsCellItem
              sx={{ '&:disabled': { color: '#465967' } }}
              disabled={params.row.usersInOrg.length > 0}
              key={'delete-org'}
              icon={<DeleteIcon />}
              onClick={() => {
                setDeleteModalTarget(params.row);
                setShowDeleteModal(true);
              }}
            />
          </span>
        </Tooltip>,
      ],
    },
  ];

  const AddOrganizationButton = useCallback(
    () => (
      <Button
        component={Link}
        to={`${path}/new`}
        startIcon={<AddIcon />}
        variant="contained"
        sx={{
          marginLeft: 'auto',
          color: '#000000',
          letterSpacing: '0.8px',
          backgroundColor: appTheme.mptLightBlue,
          '&:hover': {
            backgroundColor: appTheme.mptLightBlue,
            svg: {
              color: '#000000',
            },
          },
        }}
      >
        Provision new organization
      </Button>
    ),
    [path],
  );

  return (
    <>
      {error ? <ErrorMessage>{error}</ErrorMessage> : null}

      <StyledProvisioningPageHeading>
        Organizations {organizationsArray ? `(${organizationsArray.length})` : ''}
      </StyledProvisioningPageHeading>

      {loadingOrganizations ? <ModalLoadingSpinner /> : null}

      <Box display="flex" justifyContent="space-between" flexDirection={'column'} height={'100%'}>
        <CustomDataGrid
          rows={organizationsArray || []}
          columns={columns}
          initialState={{
            sorting: {
              sortModel: [{ field: 'group', sort: 'asc' }],
            },
          }}
          toolbarActionButtons={[<AddOrganizationButton key={'add-org-button'} />]}
          slotProps={{
            toolbar: {
              csvOptions: {
                fileName: 'Organizations',
              },
            },
          }}
          disableVirtualization={disableVirtualization}
        />
      </Box>

      <ModalDialog
        open={showDeleteModal}
        onLeftBtnClicked={() => {
          setShowDeleteModal(false);
        }}
        onRightBtnClicked={() => {
          ajaxRaw.delete(`/provisioning/organizations/${deleteModalTarget!.id}`).subscribe({
            next: value => {
              loadTable();
              setShowDeleteModal(false);
            },
            error: err => {
              loadTable();
              setError(err);
              setShowDeleteModal(false);
            },
          });
        }}
        onExited={() => {
          // Remove the delete target after the dialog's exit animation has finished. Otherwise, the `message` below
          // will briefly display `undefined` for the organization's display name
          setDeleteModalTarget(undefined);
        }}
        title={
          <Box display={'flex'} gap={'8px'} alignItems={'center'}>
            <WarningIcon htmlColor={'#FFB029'} /> Confirm Organization Deletion
          </Box>
        }
        message={
          <>
            Are you sure you want to delete <code>{deleteModalTarget?.name}</code>?
            <br />
            <br />
            This action cannot be undone.
          </>
        }
      />
    </>
  );
};

export default OrganizationsTable;
